import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { eventTypeLabel, OCCASION_CHOICES } from "../../shared/event-types";
import { hasPermission } from "../../shared/permissions";
import { PhoneError, parsePhone, sanitizePhoneInput } from "../../shared/phone";
import {
  applyFollowUp,
  emptyDraft,
  emptyFlow,
  flowDurationLabel,
  followUpCaption,
  interestDateLabel,
  parseTableNumber,
  prefillTasting,
  tableNumberUsesNumberInput,
  TastingInputError,
  visibleInterestDates,
  type ChurchChoice,
  type CrmSnapshot,
  type EvaluationCode,
  type FlowStage,
  type InterestDate,
  type MenuChoice,
  type ServiceStyle,
  type TastingDraft,
  type VenueChoice,
  DJ_CHOICES,
  DJ_LABELS,
  type DjChoice,
} from "../../shared/tasting";
import { filterOpenCards, tastingWhen, type OpenCard, type VenueFilter } from "../../shared/open-cards";
import { LEAD_STEP_COUNT } from "../../shared/inquiry";
import { ApiError, api } from "../api";
import { useSession } from "../App";
import { Banner, messageOf } from "./ui";
import { ConflictHint, useVenueConflicts, VenueConflictWarning } from "./venue-conflicts";
import { ClashSuggestions, useClashSuggestions, type SuggestionPick } from "./clash-suggestions";
import { conflictSlotDates } from "../../shared/venue-conflicts";

type Saved = {
  visit: TastingDraft & {
    id: string;
    event_id: string;
    status: "open" | "archived";
    version: number;
    newest: boolean;
    updated_at: string;
  };
  event_version: number;
  event: { id: string; title: string; sales_stage: string };
};

type EventPayload = {
  event: {
    id: string;
    title: string;
    version: number;
    event_type: string | null;
    adults: number | null;
    venue_id: string | null;
    start_date: string | null;
    date_label: string | null;
    notes: string | null;
  };
  contact: { full_name: string; given_name: string | null; family_name: string | null; phone: string | null; email: string | null } | null;
  venue: { name: string } | null;
  workbook: Record<string, string>;
};

type LookupEvent = {
  id: string;
  title: string;
  start_date: string | null;
  contact_name: string | null;
  phone: string | null;
};

const VENUES: { value: VenueChoice; label: string }[] = [
  { value: "casa", label: "CASA" },
  { value: "ktema", label: "ΚΤΗΜΑ" },
  { value: "regina", label: "REGINA" },
  { value: "other", label: "ΑΛΛΟ" },
];

const STYLES: { value: ServiceStyle; label: string }[] = [
  { value: "buffet", label: "buffet" },
  { value: "cocktail", label: "Cocktail" },
  { value: "other", label: "Άλλο" },
];

const MENUS: { value: MenuChoice; label: string }[] = [
  { value: "comfort", label: "Comfort" },
  { value: "social", label: "Social" },
  { value: "nagia", label: "Nagia" },
  { value: "other", label: "Άλλο" },
];

const RATINGS: { value: EvaluationCode; label: string; hint: string }[] = [
  { value: "TP", label: "TP", hint: "top" },
  { value: "HP", label: "HP", hint: "high" },
  { value: "MP", label: "MP", hint: "medium" },
  { value: "LP", label: "LP", hint: "low" },
  { value: "VLP", label: "VLP", hint: "very low" },
  { value: "OTHER", label: "OTHER", hint: "άλλο" },
];

const FLOW: { stage: FlowStage; label: string }[] = [
  { stage: "sales", label: "ΠΩΛΗΣΕΙΣ" },
  { stage: "tour", label: "ΠΕΡΙΗΓΗΣΗ" },
  { stage: "food", label: "ΦΑΓΗΤΟ" },
  { stage: "dessert", label: "ΓΛΥΚΟ" },
  { stage: "chef", label: "CHEF/ΑΛΛΟ" },
];

export function TastingPage() {
  const navigate = useNavigate();
  const { visitId: visitParam, eventId: eventParam } = useParams();
  const [search] = useSearchParams();
  const forceNew = search.get("new") === "1";
  const { user } = useSession();
  const canWrite = hasPermission(user.role, user.permissions, "events.write");
  const [draft, setDraft] = useState<TastingDraft>(emptyDraft);
  const [guestRaw, setGuestRaw] = useState("");
  const [visitId, setVisitId] = useState<string | null>(null);
  const [eventId, setEventId] = useState<string | null>(null);
  const [eventTitle, setEventTitle] = useState("");
  const [newest, setNewest] = useState(true);
  const [status, setStatus] = useState<"open" | "archived">("open");
  const [ready, setReady] = useState(false);
  const [phase, setPhase] = useState<"idle" | "saving" | "saved">("idle");
  const [error, setError] = useState<string | null>(null);
  const [redirect, setRedirect] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [openCards, setOpenCards] = useState<OpenCard[] | null>(null);
  const [cardQuery, setCardQuery] = useState("");
  const [venueFilter, setVenueFilter] = useState<VenueFilter | null>(null);
  const [matches, setMatches] = useState<LookupEvent[]>([]);
  const [armArchive, setArmArchive] = useState(false);
  const savedSerial = useRef("");
  const skipReload = useRef<string | null>(null);
  const draftRef = useRef(draft);
  const conflicts = useVenueConflicts(ready ? draft.venue_choice : null, draft.interest_dates, eventId, visitId);
  const suggestions = useClashSuggestions(ready ? draft.venue_choice : null, draft.interest_dates, eventId, visitId);
  const conflictSlots = new Map(conflictSlotDates(draft.interest_dates).map((entry) => [entry.date, entry.slot]));
  const visitIdRef = useRef<string | null>(null);
  const eventIdRef = useRef<string | null>(null);
  const eventVersionRef = useRef<number | null>(null);
  const versionRef = useRef<number | null>(null);
  const guestInvalid = useRef(false);
  const saving = useRef(false);
  draftRef.current = draft;
  const canEdit = canWrite && status === "open";

  useEffect(() => {
    if (visitParam || eventParam) return;
    let cancel = false;
    api<{ cards: OpenCard[] }>("/api/tastings/open")
      .then((result) => {
        if (!cancel) setOpenCards(result.cards);
      })
      .catch(() => {
        if (!cancel) setOpenCards([]);
      });
    return () => {
      cancel = true;
    };
  }, [visitParam, eventParam]);
  const dirty = ready && canEdit && JSON.stringify(draft) !== savedSerial.current;

  useEffect(() => {
    if (visitParam && skipReload.current === visitParam) {
      skipReload.current = null;
      return;
    }
    let cancel = false;
    setReady(false);
    setError(null);
    setRedirect(null);
    const load = async () => {
      if (visitParam) {
        const result = await api<Saved>(`/api/tastings/${visitParam}`);
        if (cancel) return;
        remember(result, true);
        setReady(true);
        return;
      }
      if (eventParam) {
        const listed = await api<{ visits: { id: string }[] }>(`/api/tastings?event_id=${encodeURIComponent(eventParam)}`);
        if (cancel) return;
        const latest = listed.visits[0];
        if (latest && !forceNew) {
          setRedirect(`/tasting/${latest.id}`);
          setReady(true);
          return;
        }
        const detail = await api<EventPayload>(`/api/events/${eventParam}`);
        if (cancel) return;
        const next = prefillTasting(snapshotFrom(detail), forceNew ? { tastedOn: athensToday() } : undefined);
        if (forceNew) {
          next.table_number = null;
          next.evaluation = null;
          next.evaluation_note = null;
          next.follow_up = null;
          next.follow_up_manual = false;
          next.revisit_on = null;
          next.flow = emptyFlow();
        }
        adopt(next, eventParam, detail.event.version, detail.event.title);
        setReady(true);
        return;
      }
      adopt(emptyDraft(), null, null, "");
      setReady(true);
    };
    void load().catch((reason: unknown) => {
      if (cancel) return;
      setError(messageOf(reason));
      setReady(true);
    });
    return () => {
      cancel = true;
    };
    function remember(result: Saved, replace: boolean) {
      visitIdRef.current = result.visit.id;
      eventIdRef.current = result.visit.event_id;
      eventVersionRef.current = result.event_version;
      versionRef.current = result.visit.version;
      setVisitId(result.visit.id);
      setEventId(result.visit.event_id);
      setEventTitle(result.event.title);
      setNewest(result.visit.newest);
      setStatus(result.visit.status);
      if (!replace) return;
      const next = pick(result.visit);
      draftRef.current = next;
      savedSerial.current = JSON.stringify(next);
      setDraft(next);
      setGuestRaw(next.guest_count == null ? "" : String(next.guest_count));
      guestInvalid.current = false;
    }
    function adopt(next: TastingDraft, linkedEvent: string | null, eventVersion: number | null, title: string) {
      draftRef.current = next;
      savedSerial.current = JSON.stringify(next);
      visitIdRef.current = null;
      eventIdRef.current = linkedEvent;
      eventVersionRef.current = eventVersion;
      versionRef.current = null;
      setDraft(next);
      setGuestRaw(next.guest_count == null ? "" : String(next.guest_count));
      setVisitId(null);
      setEventId(linkedEvent);
      setEventTitle(title);
      setNewest(true);
      setStatus("open");
      guestInvalid.current = false;
    }
  }, [visitParam, eventParam, forceNew]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  useEffect(() => {
    if (visitId || eventParam || eventId) return;
    const timer = window.setTimeout(() => {
      const term = query.trim();
      void api<{ events: LookupEvent[] }>(`/api/tastings/lookup${term ? `?q=${encodeURIComponent(term)}` : ""}`)
        .then((result) => setMatches(result.events))
        .catch(() => setMatches([]));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [query, visitId, eventParam, eventId]);

  function patch(partial: Partial<TastingDraft>) {
    setDraft((current) => applyFollowUp(current, partial));
  }

  function onGuests(value: string) {
    const digits = value.replace(/\D/g, "");
    setGuestRaw(digits);
    if (!digits) {
      guestInvalid.current = false;
      setError(null);
      patch({ guest_count: null });
      return;
    }
    const count = Number(digits);
    if (count < 1 || count > 100000) {
      guestInvalid.current = true;
      setError("Οι καλεσμένοι γράφονται ως ακέραιος αριθμός.");
      return;
    }
    guestInvalid.current = false;
    setError(null);
    patch({ guest_count: count });
  }

  async function persist() {
    if (!canWrite || status !== "open" || saving.current) return;
    const body = draftRef.current;
    if (!body.given_name.trim() || !body.family_name.trim()) {
      setError("Το όνομα και το επώνυμο είναι υποχρεωτικά.");
      return;
    }
    try {
      parsePhone(body.phone);
    } catch (reason) {
      if (reason instanceof PhoneError) {
        setError(reason.message);
        return;
      }
      throw reason;
    }
    if (guestInvalid.current) return;
    try {
      parseTableNumber(body.table_number);
    } catch (reason) {
      if (reason instanceof TastingInputError) {
        setError(reason.message);
        return;
      }
      throw reason;
    }
    const serial = JSON.stringify(body);
    if (serial === savedSerial.current) {
      setPhase("saved");
      setError(null);
      return;
    }
    saving.current = true;
    setPhase("saving");
    setError(null);
    try {
      const payload = {
        ...body,
        interest_dates: body.interest_dates.filter((row) => row.date),
        event_id: eventIdRef.current,
        event_version: eventVersionRef.current,
        version: versionRef.current,
      };
      const result = visitIdRef.current
        ? await api<Saved>(`/api/tastings/${visitIdRef.current}`, { method: "PATCH", body: JSON.stringify(payload) })
        : await api<Saved>("/api/tastings", { method: "POST", body: JSON.stringify(payload) });
      const previousId = visitIdRef.current;
      visitIdRef.current = result.visit.id;
      eventIdRef.current = result.visit.event_id;
      eventVersionRef.current = result.event_version;
      versionRef.current = result.visit.version;
      setVisitId(result.visit.id);
      setEventId(result.visit.event_id);
      setEventTitle(result.event.title);
      setNewest(result.visit.newest);
      if (JSON.stringify(draftRef.current) === serial) savedSerial.current = serial;
      if (previousId !== result.visit.id) {
        skipReload.current = result.visit.id;
        navigate(`/tasting/${result.visit.id}`, { replace: true });
      }
      setPhase("saved");
    } catch (reason: unknown) {
      setPhase("idle");
      if (reason instanceof ApiError && reason.status === 409 && visitIdRef.current) {
        try {
          const fresh = await api<Saved>(`/api/tastings/${visitIdRef.current}`);
          const next = pick(fresh.visit);
          draftRef.current = next;
          savedSerial.current = JSON.stringify(next);
          versionRef.current = fresh.visit.version;
          eventVersionRef.current = fresh.event_version;
          setDraft(next);
          setGuestRaw(next.guest_count == null ? "" : String(next.guest_count));
          setNewest(fresh.visit.newest);
          setError("Η καταχώριση άλλαξε και ξαναφορτώθηκε.");
        } catch (reloadError: unknown) {
          setError(messageOf(reloadError));
        }
      } else setError(messageOf(reason));
    } finally {
      saving.current = false;
    }
  }

  async function attach(id: string) {
    setError(null);
    try {
      const detail = await api<EventPayload>(`/api/events/${id}`);
      const incoming = prefillTasting(snapshotFrom(detail));
      eventIdRef.current = id;
      eventVersionRef.current = detail.event.version;
      setEventId(id);
      setEventTitle(detail.event.title);
      const current = draftRef.current;
      const empty = !current.given_name && !current.family_name && !current.phone && !current.email && current.interest_dates.length === 0 && !current.evaluation;
      if (empty) {
        draftRef.current = incoming;
        setDraft(incoming);
        setGuestRaw(incoming.guest_count == null ? "" : String(incoming.guest_count));
      }
    } catch (reason: unknown) {
      setError(messageOf(reason));
    }
  }

  async function archive() {
    if (!visitIdRef.current) return;
    setError(null);
    try {
      await api(`/api/tastings/${visitIdRef.current}/archive`, { method: "POST", body: JSON.stringify({ version: versionRef.current }) });
      setStatus("archived");
      setArmArchive(false);
    } catch (reason: unknown) {
      setError(messageOf(reason));
    }
  }

  if (redirect) return <Navigate to={redirect} replace />;

  const showSearch = ready && !visitId && !eventParam && !eventId;
  const extraOccasion = draft.occasion && !OCCASION_CHOICES.some(([value]) => value === draft.occasion) ? draft.occasion : null;

  return (
    <div className="tasting-page">
      <header className="page-head">
        <div>
          <p className="meta">{eventId ? <Link to={`/events/${eventId}`}>Εκδήλωση</Link> : <Link to="/pipeline">Εκδηλώσεις</Link>}</p>
          <h1>Γευσιγνωσία</h1>
          <p className="lede">Συμπληρώνεται στο τραπέζι, την ώρα που έρχεται ο πελάτης. Οι επιλογές είναι άγγιγμα. Η φόρμα αποθηκεύεται μόνη της και ανοίγει ξανά.</p>
        </div>
      </header>
      <Banner error={error} />
      {!canWrite ? <p className="banner">Αυτή η σύνδεση διαβάζει τη φόρμα. Δεν την αλλάζει.</p> : null}
      {status === "archived" ? <p className="banner">Αυτή η φόρμα είναι αρχειοθετημένη.</p> : null}
      {visitId && !newest && status === "open" ? <p className="banner">Τα στοιχεία αυτής της επίσκεψης μένουν στη φόρμα. Το όνομα, ο χώρος και οι καλεσμένοι ενημερώνουν την εκδήλωση.</p> : null}
      {!ready ? <p>Φόρτωση φόρμας…</p> : null}
      {showSearch ? <OpenProgress cards={openCards} query={cardQuery} venue={venueFilter} onQuery={setCardQuery} onVenue={setVenueFilter} /> : null}
      {showSearch ? (
        <article className="panel">
          <h2>Σύνδεση με υπάρχουσα εκδήλωση</h2>
          <p className="meta">Άφησέ το κενό για νέα εκδήλωση, ή διάλεξε ένα αίτημα που ήρθε από τη φόρμα.</p>
          <label>Αναζήτηση
            <input value={query} placeholder="Όνομα, τηλέφωνο, email" onChange={(event) => setQuery(event.target.value)} />
          </label>
          <div className="pick-list">
            {matches.map((event) => (
              <button key={event.id} type="button" className="pick-row" onClick={() => void attach(event.id)}>
                <strong>{event.contact_name || event.title}</strong>
                <span>{[event.start_date, event.phone].filter(Boolean).join(" · ") || "Χωρίς ημερομηνία"}</span>
              </button>
            ))}
          </div>
        </article>
      ) : null}
      {ready && eventId ? (
        <p className="meta tasting-link">
          <Link to={`/events/${eventId}`}>{eventTitle || "Η εκδήλωση"}</Link>
          {visitId && canEdit ? <Link to={`/events/${eventId}/tasting?new=1`}>Άλλη γευσιγνωσία</Link> : null}
          {!visitId && canEdit && !eventParam ? <button type="button" className="secondary" onClick={() => { eventIdRef.current = null; eventVersionRef.current = null; setEventId(null); }}>Αποσύνδεση</button> : null}
        </p>
      ) : null}
      {ready ? (
        <>
          <article className="panel">
            <h2>Στοιχεία γευστικής δοκιμής</h2>
            <div className="fields">
              <label>Ημερομηνία γευστικής δοκιμής
                <input type="date" value={draft.tasted_on ?? ""} disabled={!canEdit} onChange={(event) => patch({ tasted_on: event.target.value || null })} />
              </label>
              <label>Ώρα άφιξης
                <input type="time" value={draft.arrival_time ?? ""} disabled={!canEdit} onChange={(event) => patch({ arrival_time: event.target.value || null })} />
              </label>
              <label>Επαναληπτική επίσκεψη
                <input type="date" value={draft.revisit_on ?? ""} disabled={!canEdit} onChange={(event) => patch({ revisit_on: event.target.value || null })} />
              </label>
            </div>
          </article>

          <article className="panel">
            <h2>1) Ημερομηνίες ενδιαφέροντος</h2>
            <div className="interest-list">
              {visibleInterestDates(draft.interest_dates).map((row, index, rows) => (
                <div className="interest-row" key={index}>
                  <label>{interestDateLabel(index, rows.length)}
                    <input type="date" value={row.date} disabled={!canEdit} onChange={(event) => setInterest(index, { date: event.target.value })} />
                  </label>
                  <Choice label="μεσημέρι" pressed={row.slot === "midday"} disabled={!canEdit || !row.date} onClick={() => setInterest(index, { slot: row.slot === "midday" ? null : "midday" })} />
                  <Choice label="βράδυ" pressed={row.slot === "evening"} disabled={!canEdit || !row.date} onClick={() => setInterest(index, { slot: row.slot === "evening" ? null : "evening" })} />
                  {canEdit && index > 0 ? <button type="button" className="secondary" onClick={() => removeInterest(index)}>Αφαίρεση</button> : null}
                  <ConflictHint conflicts={row.date ? conflicts.get(row.date) : undefined} venue={draft.venue_choice} slot={row.date ? conflictSlots.get(row.date) : null} />
                </div>
              ))}
            </div>
            {canEdit ? <button type="button" className="secondary" onClick={addInterest}>+ Προσθήκη εναλλακτικής ημερομηνίας</button> : null}
          </article>

          <ClashSuggestions suggestions={suggestions} rows={visibleInterestDates(draft.interest_dates)} venue={draft.venue_choice} canEdit={canEdit} onPick={applySuggestion} />

          <article className="panel">
            <h2>2) Χώρος & Καλεσμένοι</h2>
            <div className="venue-picks">
              {VENUES.map((venue) => {
                const selected = draft.venue_choice === venue.value;
                return (
                  <div className="venue-pick" key={venue.value}>
                    <Choice label={venue.label} pressed={selected} disabled={!canEdit} onClick={() => patch({ venue_choice: selected ? null : venue.value })} />
                    {selected ? (
                      <label className="guest-count">Καλεσμένοι
                        <input type="number" inputMode="numeric" min={1} step={1} value={guestRaw} disabled={!canEdit} onChange={(event) => onGuests(event.target.value)} />
                      </label>
                    ) : null}
                  </div>
                );
              })}
            </div>
            {draft.venue_choice == null ? (
              <label>Καλεσμένοι
                <input type="number" inputMode="numeric" min={1} step={1} value={guestRaw} disabled={!canEdit} onChange={(event) => onGuests(event.target.value)} />
              </label>
            ) : null}
            {draft.venue_choice === "other" ? (
              <label>Άλλος χώρος
                <input value={draft.venue_other ?? ""} disabled={!canEdit} onChange={(event) => patch({ venue_other: event.target.value || null })} />
              </label>
            ) : null}
            <VenueConflictWarning conflicts={conflicts} venue={draft.venue_choice} guests={draft.guest_count} slots={conflictSlots} />
          </article>

          <article className="panel">
            <h2>3) Γεγονός</h2>
            <div className="choice-row">
              {OCCASION_CHOICES.map(([value, label]) => (
                <Choice key={value} label={label} pressed={draft.occasion === value} disabled={!canEdit} onClick={() => patch({ occasion: draft.occasion === value ? null : value })} />
              ))}
              {extraOccasion ? <Choice label={eventTypeLabel(extraOccasion) || extraOccasion} pressed disabled={!canEdit} onClick={() => patch({ occasion: null })} /> : null}
            </div>
            {draft.occasion === "other" ? (
              <label className="other-field">Άλλο
                <input value={draft.occasion_other ?? ""} disabled={!canEdit} onChange={(event) => patch({ occasion_other: event.target.value || null })} />
              </label>
            ) : null}
            <h3>Τύπος εκδήλωσης</h3>
            <div className="choice-row">
              {STYLES.map((option) => (
                <Choice key={option.value} label={option.label} pressed={draft.service_style === option.value} disabled={!canEdit} onClick={() => patch({ service_style: draft.service_style === option.value ? null : option.value })} />
              ))}
            </div>
            {draft.service_style === "other" ? (
              <label className="other-field">Άλλο
                <input value={draft.service_style_other ?? ""} disabled={!canEdit} onChange={(event) => patch({ service_style_other: event.target.value || null })} />
              </label>
            ) : null}
          </article>

          <article className="panel">
            <h2>4) MENU – ΕΚΚΛΗΣΙΑ - DJ</h2>
            <div className="choice-row">
              {MENUS.map((option) => (
                <Choice key={option.value} label={option.label} pressed={draft.menu_choice === option.value} disabled={!canEdit} onClick={() => patch({ menu_choice: draft.menu_choice === option.value ? null : option.value })} />
              ))}
            </div>
            {draft.menu_choice === "other" ? (
              <label className="other-field">Άλλο μενού
                <input value={draft.menu_other ?? ""} disabled={!canEdit} onChange={(event) => patch({ menu_other: event.target.value || null })} />
              </label>
            ) : null}
            <h3>Εκκλησία</h3>
            <div className="choice-row">
              <Choice label="Εντός" pressed={draft.church === "on_site"} disabled={!canEdit} onClick={() => patch({ church: toggleChurch(draft.church, "on_site") })} />
              <Choice label="Εκτός" pressed={draft.church === "off_site"} disabled={!canEdit} onClick={() => patch({ church: toggleChurch(draft.church, "off_site") })} />
            </div>
            {draft.church === "off_site" ? (
              <label className="other-field">Τοποθεσία
                <input value={draft.church_place ?? ""} disabled={!canEdit} onChange={(event) => patch({ church_place: event.target.value || null })} />
              </label>
            ) : null}
            <DjChoices value={draft.dj} disabled={!canEdit} onChange={(dj) => patch({ dj })} />
            <YesNo label="Ορχήστρα" value={draft.orchestra} disabled={!canEdit} onChange={(orchestra) => patch({ orchestra })} />
            <YesNo label="Πυροτεχνήματα" value={draft.fireworks} disabled={!canEdit} onChange={(fireworks) => patch({ fireworks })} />
            <YesNo label="Ανιματέρ" value={draft.animator} disabled={!canEdit} onChange={(animator) => patch({ animator })} />
            <YesNo label="Παιχνίδια" value={draft.games} disabled={!canEdit} onChange={(games) => patch({ games })} />
            <label className="other-field">Άλλο
              <input value={draft.extras ?? ""} disabled={!canEdit} onChange={(event) => patch({ extras: event.target.value || null })} />
            </label>
          </article>

          <article className="panel">
            <h2>5) Στοιχεία ενδιαφερομένου</h2>
            <div className="fields">
              <label>Όνομα
                <input value={draft.given_name} disabled={!canEdit} onChange={(event) => patch({ given_name: event.target.value })} />
              </label>
              <label>Επώνυμο
                <input value={draft.family_name} disabled={!canEdit} onChange={(event) => patch({ family_name: event.target.value })} />
              </label>
              <label>Τηλέφωνο
                <input type="tel" inputMode="tel" value={draft.phone ?? ""} disabled={!canEdit} onChange={(event) => patch({ phone: sanitizePhoneInput(event.target.value) || null })} />
              </label>
              <label>e-mail address
                <input inputMode="email" autoComplete="email" value={draft.email ?? ""} disabled={!canEdit} onChange={(event) => patch({ email: event.target.value || null })} />
              </label>
            </div>
            <label>Ειδικά αιτήματα
              <textarea value={draft.special_requests ?? ""} disabled={!canEdit} onChange={(event) => patch({ special_requests: event.target.value || null })} />
            </label>
          </article>

          <article className="panel">
            <h2>ΑΡΙΘΜΟΣ ΤΡΑΠΕΖΙΟΥ</h2>
            <label className="table-number">Τραπέζι
              <input
                type={tableNumberUsesNumberInput(draft.table_number) ? "number" : "text"}
                inputMode="numeric"
                min={1}
                max={60}
                step={1}
                value={draft.table_number ?? ""}
                disabled={!canEdit}
                onChange={(event) => patch({ table_number: event.target.value || null })}
              />
            </label>
            <p className="meta">ΚΑΤΩΤΕΡΩ Η ΡΟΗ ΤΗΣ ΕΠΙΣΚΕΨΗΣ</p>
            <div className="flow-grid">
              {FLOW.map((item) => {
                const duration = flowDurationLabel(draft.flow[item.stage], draft.flow_ends[item.stage]);
                return (
                  <div className="flow-cell" key={item.stage}>
                    <strong>{item.label}</strong>
                    <label>ΈΝΑΡΞΗ
                      <input type="time" value={draft.flow[item.stage] ?? ""} disabled={!canEdit} onChange={(event) => setFlowTime("flow", item.stage, event.target.value || null)} />
                    </label>
                    {canEdit ? <button type="button" className="now" onClick={() => setFlowTime("flow", item.stage, athensNow())}>Τώρα</button> : null}
                    <label>ΤΕΛΟΣ
                      <input type="time" value={draft.flow_ends[item.stage] ?? ""} disabled={!canEdit} onChange={(event) => setFlowTime("flow_ends", item.stage, event.target.value || null)} />
                    </label>
                    {canEdit ? <button type="button" className="now" onClick={() => setFlowTime("flow_ends", item.stage, athensNow())}>Τώρα</button> : null}
                    {duration ? <p className="flow-duration">{duration}</p> : null}
                  </div>
                );
              })}
            </div>
          </article>

          <article className="panel">
            <h2>EVLTN</h2>
            <p className="meta">Ενδιαφέρον του πελάτη. Διαλέγεις ένα.</p>
            <div className="choice-row">
              {RATINGS.map((rating) => (
                <Choice
                  key={rating.value}
                  label={rating.label}
                  hint={rating.hint}
                  pressed={draft.evaluation === rating.value}
                  disabled={!canEdit}
                  onClick={() => {
                    const next = draft.evaluation === rating.value ? null : rating.value;
                    patch({ evaluation: next, evaluation_note: next === "OTHER" ? draft.evaluation_note : null });
                  }}
                />
              ))}
            </div>
            {draft.evaluation === "OTHER" ? (
              <label className="other-field">Άλλο
                <input value={draft.evaluation_note ?? ""} disabled={!canEdit} onChange={(event) => patch({ evaluation_note: event.target.value || null })} />
              </label>
            ) : null}
            <label className="follow-up">
              <span>{draft.follow_up ? followUpCaption(draft.follow_up) : "Follow-up"}</span>
              <input type="date" value={draft.follow_up ?? ""} disabled={!canEdit} onChange={(event) => patch({ follow_up: event.target.value || null })} />
            </label>
          </article>

          <div className="tasting-bar">
            <p className="save-state" aria-live="polite">{phase === "saving" ? "Αποθήκευση…" : phase === "saved" && !dirty ? "Αποθηκεύτηκε" : ""}</p>
            <div className="actions">
              {canEdit ? <button type="button" onClick={() => void persist()} disabled={phase === "saving"}>Αποθήκευση</button> : null}
              {canEdit && visitId ? (
                armArchive
                  ? <button type="button" className="danger" onClick={() => void archive()}>Αρχειοθέτηση</button>
                  : <button type="button" className="secondary" onClick={() => setArmArchive(true)}>Αρχείο</button>
              ) : null}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );

  function applySuggestion(pick: SuggestionPick) {
    setDraft((current) => {
      const base = pick.venue ? applyFollowUp(current, { venue_choice: pick.venue, venue_other: null }) : current;
      return {
        ...base,
        interest_dates: visibleInterestDates(base.interest_dates).map((row, rowIndex) => (rowIndex === pick.index ? { ...row, ...(pick.date ? { date: pick.date } : {}), ...(pick.slot !== undefined ? { slot: pick.slot } : {}) } : row)),
      };
    });
  }

  function setInterest(index: number, partial: Partial<InterestDate>) {
    setDraft((current) => ({
      ...current,
      interest_dates: visibleInterestDates(current.interest_dates).map((row, rowIndex) => rowIndex === index ? { ...row, ...partial } : row),
    }));
  }

  function addInterest() {
    setDraft((current) => ({ ...current, interest_dates: [...visibleInterestDates(current.interest_dates), { date: "", slot: null }] }));
  }

  function removeInterest(index: number) {
    if (index === 0) return;
    setDraft((current) => ({ ...current, interest_dates: visibleInterestDates(current.interest_dates).filter((_, rowIndex) => rowIndex !== index) }));
  }

  function setFlowTime(key: "flow" | "flow_ends", stage: FlowStage, value: string | null) {
    setDraft((current) => ({ ...current, [key]: { ...current[key], [stage]: value } }));
  }
}

function DjChoices({ value, disabled, onChange }: { value: DjChoice | null; disabled?: boolean; onChange: (next: DjChoice | null) => void }) {
  return (
    <>
      <h3>DJ</h3>
      <div className="choice-row">
        {DJ_CHOICES.map((choice) => (
          <Choice key={choice} label={DJ_LABELS[choice]} pressed={value === choice} disabled={disabled} onClick={() => onChange(value === choice ? null : choice)} />
        ))}
      </div>
    </>
  );
}

function YesNo({ label, value, disabled, onChange }: { label: string; value: boolean | null; disabled?: boolean; onChange: (next: boolean | null) => void }) {
  return (
    <>
      <h3>{label}</h3>
      <div className="choice-row">
        <Choice label="Ναι" pressed={value === true} disabled={disabled} onClick={() => onChange(value === true ? null : true)} />
        <Choice label="Όχι" pressed={value === false} disabled={disabled} onClick={() => onChange(value === false ? null : false)} />
      </div>
    </>
  );
}

const VENUE_FILTERS: { value: VenueFilter | null; label: string }[] = [
  { value: null, label: "Όλα" },
  { value: "casa", label: "CASA" },
  { value: "ktema", label: "ΚΤΗΜΑ" },
  { value: "regina", label: "REGINA" },
  { value: "other", label: "ΑΛΛΟ" },
];

function OpenProgress({ cards, query, venue, onQuery, onVenue }: {
  cards: OpenCard[] | null;
  query: string;
  venue: VenueFilter | null;
  onQuery: (value: string) => void;
  onVenue: (value: VenueFilter | null) => void;
}) {
  const visible = cards ? filterOpenCards(cards, query, venue) : [];
  return (
    <article className="panel open-progress">
      <h2>Σε εξέλιξη</h2>
      <label>Αναζήτηση
        <input value={query} placeholder="Όνομα" onChange={(event) => onQuery(event.target.value)} />
      </label>
      <div className="choice-row">
        {VENUE_FILTERS.map((option) => (
          <button key={option.label} type="button" className="choice" aria-pressed={venue === option.value} onClick={() => onVenue(option.value)}>
            {option.label}
          </button>
        ))}
      </div>
      {!cards ? <p className="meta">Φόρτωση καρτών…</p> : null}
      {cards && visible.length === 0 ? <p className="meta">Καμία κάρτα σε εξέλιξη</p> : null}
      <div className="open-cards">
        {visible.map((card) => (
          <Link className="open-card" key={card.id} to={card.href}>
            <strong>{card.name}</strong>
            <span className={`step-badge s${card.step}`}>Step {card.step}/{LEAD_STEP_COUNT}</span>
            <span>{tastingWhen(card.tasted_on, card.arrival_time)}</span>
            <span>{card.venue}</span>
          </Link>
        ))}
      </div>
    </article>
  );
}

function Choice({ label, hint, pressed, disabled, onClick }: { label: string; hint?: string; pressed: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <button type="button" className="choice" aria-pressed={pressed} disabled={disabled} onClick={onClick}>
      <span>{label}</span>
      {hint ? <small>{hint}</small> : null}
    </button>
  );
}

function toggleChurch(current: ChurchChoice | null, value: ChurchChoice): ChurchChoice | null {
  return current === value ? null : value;
}

function pick(visit: Saved["visit"]): TastingDraft {
  return {
    tasted_on: visit.tasted_on,
    arrival_time: visit.arrival_time,
    revisit_on: visit.revisit_on,
    interest_dates: visit.interest_dates,
    venue_choice: visit.venue_choice,
    venue_other: visit.venue_other,
    guest_count: visit.guest_count,
    occasion: visit.occasion,
    occasion_other: visit.occasion_other,
    service_style: visit.service_style,
    service_style_other: visit.service_style_other,
    menu_choice: visit.menu_choice,
    menu_other: visit.menu_other,
    church: visit.church,
    church_place: visit.church_place,
    dj: visit.dj,
    orchestra: visit.orchestra,
    fireworks: visit.fireworks,
    animator: visit.animator,
    games: visit.games,
    extras: visit.extras,
    given_name: visit.given_name,
    family_name: visit.family_name,
    phone: visit.phone,
    email: visit.email,
    special_requests: visit.special_requests,
    table_number: visit.table_number,
    flow: visit.flow,
    flow_ends: visit.flow_ends ?? emptyFlow(),
    evaluation: visit.evaluation,
    evaluation_note: visit.evaluation_note,
    follow_up: visit.follow_up,
    follow_up_manual: visit.follow_up_manual,
  };
}

function snapshotFrom(detail: EventPayload): CrmSnapshot {
  return {
    title: detail.event.title,
    event_type: detail.event.event_type,
    adults: detail.event.adults,
    venue_id: detail.event.venue_id,
    venue_name: detail.venue?.name ?? null,
    start_date: detail.event.start_date,
    date_label: detail.event.date_label,
    notes: detail.event.notes,
    contact_name: detail.contact?.full_name ?? null,
    given_name: detail.contact?.given_name ?? null,
    family_name: detail.contact?.family_name ?? null,
    phone: detail.contact?.phone ?? null,
    email: detail.contact?.email ?? null,
    workbook: detail.workbook ?? {},
  };
}

function athensNow(): string {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Athens", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date());
  let hour = parts.find((part) => part.type === "hour")?.value ?? "00";
  if (hour === "24") hour = "00";
  const minute = parts.find((part) => part.type === "minute")?.value ?? "00";
  return `${hour.slice(-2)}:${minute}`;
}

function athensToday(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Athens", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}
